import { useEffect, useMemo, useState } from 'react';
import { Card, Field } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { listenNotificationDevices, listenNotificationPreferences, saveNotificationPreferences } from '../../firebase/repo';
import { browserPushSupport, disableBrowserPush, enableBrowserPush } from '../../firebase/messaging';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { defaultNotificationPreferences, normalizeNotificationPreferences } from '../../utils/notificationDelivery';
import type { NotificationDevice, NotificationFamily, NotificationPreferences } from '../../types';

const families: Array<{ key: NotificationFamily; label: string }> = [
  { key: 'payments', label: 'Payments' },
  { key: 'accounts', label: 'Accounts & Cards' },
  { key: 'budgets', label: 'Budgets' },
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'health', label: 'Financial Health' }
];

export function NotificationDeliverySettings({ uid, email }: { uid: string; email: string }) {
  const toast = useToast();
  const [prefs, setPrefs] = useState<NotificationPreferences>(() => defaultNotificationPreferences(email));
  const [devices, setDevices] = useState<NotificationDevice[]>([]);
  const [support, setSupport] = useState<{ supported: boolean; permission: string; vapidConfigured: boolean }>();
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  useEffect(() => listenNotificationPreferences(uid, email, setPrefs), [uid, email]);
  useEffect(() => listenNotificationDevices(uid, setDevices), [uid]);
  useEffect(() => { browserPushSupport().then(setSupport).catch(() => setSupport({ supported: false, permission: 'unsupported', vapidConfigured: false })); }, []);

  const activeDevices = useMemo(() => devices.filter((device) => device.enabled !== false), [devices]);
  const save = async (patch: Partial<NotificationPreferences>) => {
    const next = normalizeNotificationPreferences({ ...prefs, ...patch }, email);
    setPrefs(next);
    setSaving(true);
    try {
      await saveNotificationPreferences(uid, next, email);
      toast('Notification settings saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Notification settings save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleFamily = (key: NotificationFamily) => save({ families: { ...prefs.families, [key]: !prefs.families[key] } });

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await enableBrowserPush(uid);
      await save({ pushEnabled: true });
      setSupport(await browserPushSupport());
      toast('Browser notifications enabled');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Browser notification setup failed', 'error');
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      await disableBrowserPush(uid);
      await save({ pushEnabled: false });
      setSupport(await browserPushSupport());
      toast('Browser notifications disabled');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Browser notification disable failed', 'error');
    } finally {
      setPushBusy(false);
    }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const callable = httpsCallable(functions, 'sendTestNotificationDelivery');
      await callable({});
      toast('Test notification request completed');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Test notification failed', 'error');
    } finally {
      setTestBusy(false);
    }
  };

  return <Card className="settings-manager-card notification-settings-card">
    <div className="section-title">
      <div><h2>Notifications</h2><p className="muted">Control external delivery. The in-app Notification Center remains the main alert source.</p></div>
      <span className="pill">{activeDevices.length} device{activeDevices.length === 1 ? '' : 's'}</span>
    </div>

    <div className="settings-form-panel">
      <div className="toggle-row"><div><b>In-app notifications</b><span>Critical alerts remain visible until fixed.</span></div><input type="checkbox" checked={prefs.inAppEnabled} onChange={(e) => save({ inAppEnabled: e.target.checked })} /></div>
      <div className="toggle-row"><div><b>Email notifications</b><span>{email ? maskEmail(email) : 'No Firebase Auth email found'}</span></div><input type="checkbox" checked={prefs.emailEnabled} disabled={!email || saving} onChange={(e) => save({ emailEnabled: e.target.checked, emailAddress: email })} /></div>
    </div>

    <div className="settings-form-panel">
      <div className="section-title compact"><div><h3>Browser Push</h3><p className="muted">Permission prompt appears only after pressing Enable. iPhone support may require the installed PWA experience.</p></div></div>
      <div className="settings-summary-strip">
        <span><b>{support?.supported ? 'Supported' : 'Not supported'}</b> browser</span>
        <span><b>{support?.permission || 'checking'}</b> permission</span>
        <span><b>{support?.vapidConfigured ? 'Ready' : 'Missing'}</b> VAPID key</span>
      </div>
      <div className="confirm-actions">
        <button className="btn primary" disabled={pushBusy || !support?.supported || !support?.vapidConfigured} onClick={enablePush}>{pushBusy ? 'Working...' : 'Enable Browser Notifications'}</button>
        <button className="btn" disabled={pushBusy || !activeDevices.length} onClick={disablePush}>Disable Browser Notifications</button>
        <button className="btn" disabled={testBusy} onClick={sendTest}>{testBusy ? 'Sending...' : 'Send test notification'}</button>
      </div>
    </div>

    <div className="grid2">
      <Field label="Push preview">
        <select value={prefs.pushPreview} onChange={(e) => save({ pushPreview: e.target.value as NotificationPreferences['pushPreview'] })}>
          <option value="private">Private</option>
          <option value="detailed">Detailed</option>
        </select>
      </Field>
      <Field label="Quiet hours">
        <select value={prefs.quietHoursEnabled ? 'on' : 'off'} onChange={(e) => save({ quietHoursEnabled: e.target.value === 'on' })}>
          <option value="off">Off</option>
          <option value="on">On for non-critical push</option>
        </select>
      </Field>
      <Field label="Quiet start"><input type="time" value={prefs.quietStart} onChange={(e) => save({ quietStart: e.target.value })} /></Field>
      <Field label="Quiet end"><input type="time" value={prefs.quietEnd} onChange={(e) => save({ quietEnd: e.target.value })} /></Field>
      <Field label="Timezone"><input value={prefs.timezone || 'Asia/Kolkata'} onChange={(e) => save({ timezone: e.target.value || 'Asia/Kolkata' })} /></Field>
    </div>

    <div className="settings-form-panel">
      <h3>Alert Families</h3>
      {families.map((family) => <label className="toggle-row" key={family.key}>
        <div><b>{family.label}</b><span>Controls push/email delivery; in-app alert generation is unchanged.</span></div>
        <input type="checkbox" checked={prefs.families[family.key]} onChange={() => toggleFamily(family.key)} />
      </label>)}
    </div>

    <p className="empty-state">Email delivery is server-side only. Configure Firebase Functions delivery mode and mail provider before enabling live sends.</p>
  </Card>;
}

function maskEmail(email: string) {
  const [name, domain] = email.split('@');
  return `${name.slice(0, 2)}***@${domain || 'email'}`;
}
